/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file needs proper typing with SupabaseClient when dependencies are resolved
// Currently using type assertions as a temporary solution

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BillingContextType,
  BillingSubscription,
  BillingCustomer,
  BillingProduct,
  BillingPrice,
  PlanType,
  PlanCapabilities,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  PortalSessionResponse,
} from './types';

const BillingContext = createContext<BillingContextType | null>(null);

interface BillingProviderProps {
  children: React.ReactNode;
  supabaseClient: any; // Temporary - will be properly typed when SupabaseClient is available
  supabaseUrl: string;
}

const PLAN_CAPABILITIES: Record<PlanType, PlanCapabilities> = {
  free: {
    canEmbedNative: false,
    canSaveProjects: false,
    canAccess3DShaders: false,
    maxProjects: 1,
    hasCustomSupport: false,
    hasAdvancedExport: false,
  },
  pro: {
    canEmbedNative: true,
    canSaveProjects: true,
    canAccess3DShaders: true,
    maxProjects: Infinity,
    hasCustomSupport: true,
    hasAdvancedExport: true,
  },
};

export function BillingProvider({
  children,
  supabaseClient,
  supabaseUrl,
}: BillingProviderProps) {
  const queryClient = useQueryClient();

  // Fetch user's subscription
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery<BillingSubscription | null>({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from('subscriptions')
        .select('*')
        .eq('user_id', (await supabaseClient.auth.getUser()).data.user?.id)
        .in('status', ['active', 'trialing', 'past_due'])
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned
        throw error;
      }

      return data || null;
    },
    enabled: !!supabaseClient,
  });

  // Fetch user's customer record
  const { data: customer } = useQuery<BillingCustomer | null>({
    queryKey: ['customer'],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from('customers')
        .select('*')
        .eq('id', (await supabaseClient.auth.getUser()).data.user?.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || null;
    },
    enabled: !!supabaseClient,
  });

  // Fetch products
  const { data: products = [] } = useQuery<BillingProduct[]>({
    queryKey: ['products'],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from('products')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!supabaseClient,
  });

  // Fetch prices
  const { data: prices = [] } = useQuery<BillingPrice[]>({
    queryKey: ['prices'],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from('prices')
        .select('*')
        .eq('active', true)
        .order('unit_amount');

      if (error) throw error;
      return data || [];
    },
    enabled: !!supabaseClient,
  });

  // Determine current plan
  const plan: PlanType = subscription?.status === 'active' ? 'pro' : 'free';
  const capabilities = PLAN_CAPABILITIES[plan];

  // Create checkout session mutation
  const createCheckoutMutation = useMutation({
    mutationFn: async ({
      priceId,
      successUrl,
      cancelUrl,
    }: CheckoutSessionRequest & { successUrl: string; cancelUrl: string }) => {
      const {
        data: { session },
      } = await supabaseClient.auth.getSession();

      const response = await fetch(
        `${supabaseUrl}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ priceId, successUrl, cancelUrl }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      return response.json() as Promise<CheckoutSessionResponse>;
    },
  });

  // Create portal session mutation
  const createPortalMutation = useMutation({
    mutationFn: async (returnUrl: string) => {
      const {
        data: { session },
      } = await supabaseClient.auth.getSession();

      const response = await fetch(
        `${supabaseUrl}/functions/v1/create-portal-session`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ returnUrl }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create portal session');
      }

      return response.json() as Promise<PortalSessionResponse>;
    },
  });

  const createCheckoutSession = async (
    priceId: string
  ): Promise<CheckoutSessionResponse> => {
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/dashboard?success=true`;
    const cancelUrl = `${baseUrl}/pricing?canceled=true`;

    return createCheckoutMutation.mutateAsync({
      priceId,
      successUrl,
      cancelUrl,
    });
  };

  const createPortalSession = async (): Promise<PortalSessionResponse> => {
    const returnUrl = `${window.location.origin}/dashboard`;
    return createPortalMutation.mutateAsync(returnUrl);
  };

  const refreshSubscription = async () => {
    await queryClient.invalidateQueries({ queryKey: ['subscription'] });
    await queryClient.invalidateQueries({ queryKey: ['customer'] });
  };

  const isLoading = subscriptionLoading;
  const error = subscriptionError as Error | null;

  const value: BillingContextType = {
    subscription: subscription || null,
    customer: customer || null,
    plan,
    capabilities,
    products,
    prices,
    isLoading,
    error,
    createCheckoutSession,
    createPortalSession,
    refreshSubscription,
  };

  return (
    <BillingContext.Provider value={value}>{children}</BillingContext.Provider>
  );
}

export function useBilling(): BillingContextType {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
}
